/**
 * @flow
 */

import Messenger from 'lib/messenger';
import * as presence from 'lib/presence';

import { CheckoutWidget, CalendarWidget, TicketWidget, ListingsWidget } from './widget';
import { buildEvent } from './util';

import type {
  UniversePopupWidget as PopupWidget,
  UniverseEmbedWidget as EmbedWidget,
  WidgetInitState
} from './widget';


//-----------------------------------------------------------------------------
// Messenger payload types
//-----------------------------------------------------------------------------

type ShowCheckoutPayload = {
  targetId?: string;
  targetSlug?: string;
  skipQueue?: boolean;
  widget?: string;
  q?: string;
  ts?: string;
  qsession?: string;
};

type ResizePayload = {
  height: string;
  target: string;
};

type ScrollTopPayload = {
  target: string;
  height: string;
}

type ScrollToPayload = {
  x: number;
  y: number;
}

//-----------------------------------------------------------------------------
// Orchestrator
//-----------------------------------------------------------------------------

export default class Orchestrator {
  _activePopup: ?PopupWidget;
  _messenger: Messenger;
  _widgets: Array<EmbedWidget>;
  showCheckout: (targetId: string, state?: Object) => boolean;
  _handleGA: (e: MessageEvent) => void;
  _gaID: string;

  constructor() {
    this._activePopup = null;
    this._messenger = new Messenger(window);
    this._widgets = [];
    this._gaID = '';

    // public methods
    this.showCheckout = this.showCheckout.bind(this);

    // listeners
    this._messenger.on('CHECKOUT:PURCHASED', this._purchasedCheckout);
    this._messenger.on('CHECKOUT:SHOW', this._showCheckout.bind(this));
    this._messenger.on('POPUP:HIDE', this._hidePopup.bind(this));
    this._messenger.on('EMBED:RESIZE', this._resizeEmbed.bind(this));
    this._messenger.on('EMBED:SCROLL_TOP', this._scrollTop.bind(this));
    this._messenger.on('EMBED:SCROLL_TO', this._scrollTo.bind(this));
    this._messenger.on('GOOGLE_ADS:EMIT_EVENT', this._emitGoogleAdsEvent.bind(this));
    this._messenger.on('GOOGLE_ANALYTICS:EMIT_EVENT', this._emitGoogleAnalyticsEvent.bind(this));
    this._messenger.on('FACEBOOK_PIXEL:EMIT_EVENT', this._emitFacebookPixelEvent.bind(this));
    this._messenger.on('TIKTOK_PIXEL:EMIT_EVENT', this._emitTiktokPixelEvent.bind(this));
    this._messenger.on('ANALYTICS:EMIT_EVENT', this._emitAnalyticsEvent.bind(this));

    // helpers
    this._handleGA = this._handleGA.bind(this);
  }

  showCheckout(targetId: string, state?: Object): boolean {
    return this._showCheckout({ ...state, targetId });
  }

  _purchasedCheckout(data: any): void {
    document.dispatchEvent(buildEvent('ticket:purchased', data));
  }

  _showCheckout(payload: ShowCheckoutPayload): boolean {
    if (this._activePopup) {
      return false;
    }

    const { targetId, targetSlug, skipQueue, widget, q, ts, qsession, ...rest }: ShowCheckoutPayload = payload;
    const id: string = targetId || targetSlug || '';
    const normalizedQsession = qsession || undefined;

    const initState: WidgetInitState = { ...rest, targetId: id, targetType: 'event', skipQueue, q, ts, qsession: normalizedQsession, widget: widget || 'button' };
    const co: CheckoutWidget = new CheckoutWidget(initState);

    co.attach();
    document.dispatchEvent(buildEvent('opened'));

    // The edge already counts checkout's /{slug} page view; pinging too would
    // double-count the fan.
    presence.pauseAll();

    this._activePopup = co;

    // in case the widget doesn't have cookie access
    window.addEventListener('message', this._handleGA);

    return true;
  }

  _handleGA(e: MessageEvent): void {
    if (window.dataLayer) {
      const data = e.data;
      if (data && this._messenger && this._activePopup) {
        if (data.type === 'IFRAME_GA_READY') {
          this._messenger.send(this._activePopup.window(), 'PARENT_GA_READY', { hasDatalayer: !!(window.dataLayer) })
          .then((data) => {
            if (data.send_to) {
              this._gaID = data.send_to;
              this._pushData('event', 'page_view', data);
            }
          })
          .catch();
        }
        else if (typeof data === 'object' && data.type === 'IFRAME_PURCHASE_READY' && this._gaID) {
          const dataToSend = data.payload;
          if (dataToSend && typeof dataToSend === 'object') {
            dataToSend.send_to = this._gaID;
            this._pushData('event', 'purchase', dataToSend);
          }
        }
      }
    }
  }

  // $FlowIssue
  _pushData = function() {dataLayer.push(arguments);}

  _hidePopup(): boolean {
    if (!this._activePopup) {
      return false;
    }

    this._activePopup.detach();
    document.dispatchEvent(buildEvent('closed'));

    this._activePopup = null;
    this._gaID = '';
    window.removeEventListener('message', this._handleGA);

    presence.resumeAll();
    return true;
  }

  _resizeEmbed(payload: ResizePayload): boolean {
    const { height, target }: ResizePayload = payload;
    for (let i = 0; i < this._widgets.length; i ++) {
      if (!target || this._widgets[i].id() === target) {
        this._widgets[i].resize(height);
        return true;
      }
    }
    return false;
  }

  _scrollTop(payload: ScrollTopPayload): boolean {
    const { target }: ScrollTopPayload = payload;
    for (let i = 0; i < this._widgets.length; i ++) {
      if (this._widgets[i].id() === target) {
        window.location.href = `#universe-${target}`;
        return true;
      }
    }
    return false;
  }

  _scrollTo(payload: ScrollToPayload): boolean {
    const { x, y }: ScrollToPayload = payload;
    window.scrollTo(x, y);
    return true;
  }

  _emitGoogleAdsEvent = (data: any): void => {
    document.dispatchEvent(buildEvent('tracking:googleads', data));
  }

  _emitGoogleAnalyticsEvent = (data: any): void => {
    document.dispatchEvent(buildEvent('tracking:googleanalytics', data));
  }

  _emitFacebookPixelEvent = (data: any): void => {
    document.dispatchEvent(buildEvent('tracking:metapixel', data));
  }

  _emitTiktokPixelEvent = (data: any): void => {
    document.dispatchEvent(buildEvent('tracking:tiktokpixel', data));
  }

  _emitAnalyticsEvent = (data: any): void => {
    document.dispatchEvent(buildEvent('tracking:analytics', data));
  }

  register(container: Element, widgetType: string, initState: WidgetInitState): ?EmbedWidget {
    let widget: ?EmbedWidget;
    switch (widgetType) {
      case 'universe-calendar':
        widget = new CalendarWidget(container, initState);
        break;
      case 'universe-ticket':
        widget = new TicketWidget(container, initState);
        break;
      case 'universe-listings':
        widget = new ListingsWidget(container, initState);
        break;
      default:
        widget = null;
    }

    if (widget) {
      this._widgets.push(widget);

      // The edge page-view sensor doesn't match /tickets/{id} or /calendars/{id},
      // so ping for them. Listings and multi-time-slot calendars are excluded:
      // their targetId is the host account, not a listing.
      const singleListingCalendar =
        widgetType === 'universe-calendar' && initState.targetType === 'time-slots';
      const embedsQueueableEvent = widgetType === 'universe-ticket' || singleListingCalendar;
      if (
        __EMBED_VIA_CHECKOUT__ &&
        embedsQueueableEvent &&
        !initState.skipQueue &&
        !initState.skipPresence
      ) {
        presence.start(initState.targetId, 'widget');
      }
    }

    return widget;
  }
}



// WEBPACK FOOTER //
// ./src/bootstrap/orchestrator.js